import {
  createApplePayMerchatSession,
  createApplePayPayment,
  getCheckoutSession,
  isIdentifiedCustomer,
} from 'api/checkout';
import { useConfig } from 'config/use-config';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import { useCheckout } from 'hooks/checkout';
import { EffectiveCart } from 'interfaces/cart';
import { ApplePaymentPaymentRequest } from 'interfaces/checkout';
import { useRouter } from 'next/router';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { showErrorToast } from 'utils/toasts';

export interface ApplePayPaymentDataPayload {
  checkoutId: string;
  items?: EffectiveCart['items'];
  discount: EffectiveCart['discount'];
  shippingMethod: string;
}

interface Output {
  request: ApplePayJS.ApplePayPaymentRequest | null;
  applePaySession: MutableRefObject<ApplePaySession | null>;
  handleApplePayEvents: (applePaySession: ApplePaySession) => void;
}

const useApplePayButton = (effectiveCart?: EffectiveCart): Output => {
  const { currencyCode, shopId, shippingCountries, legalName } = useConfig();
  const { reFetchEffectiveCart, setIsApplePayModalOpen, setExpressCheckoutCustomerId } = useCart();
  const applePaySession = useRef<ApplePaySession | null>(null);
  const [itemsAmount, setItemsAmount] = useState(0);
  const [currentTotalAmount, setCurrentTotalAmount] = useState(0);
  const { push } = useRouter();
  const { formatDate, formatMessage } = useIntl();
  const { customer } = useAuth();
  const { checkout } = useCheckout({
    customer,
  });

  const shippingMethods = useMemo(
    () =>
      effectiveCart?.shippingOptions.methods.map((method) => {
        return {
          label: method.type,
          amount: (method.price / 100).toFixed(2),
          detail: `${formatMessage({
            id: 'express-checkout:summary-form:shipping-and-payment:shipping',
          })}: ${formatDate(method.eta, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}`,
          identifier:
            method.type === 'Digital'
              ? 'digital'
              : method.carrierCode.replace(/\s+/g, '-').toLowerCase(),
        };
      }),
    [effectiveCart, formatDate, formatMessage]
  );

  const payload = useRef<ApplePayPaymentDataPayload>({
    checkoutId: checkout.id,
    items: effectiveCart?.items,
    discount: effectiveCart?.discount,
    shippingMethod: '',
  });

  useEffect(() => {
    if (shippingMethods && effectiveCart) {
      payload.current.shippingMethod = shippingMethods[0].label;
      setItemsAmount((effectiveCart?.itemsTotal ?? 0) / 100);
      setCurrentTotalAmount((effectiveCart?.cartTotal ?? 0) / 100);
    }
    if (checkout.id) {
      payload.current.checkoutId = checkout.id;
    }
    if (effectiveCart?.items) {
      payload.current.items = effectiveCart?.items;
    }
    if (effectiveCart?.discount) {
      payload.current.discount = effectiveCart?.discount;
    }
  }, [checkout.id, effectiveCart, shippingMethods]);

  const request: ApplePayJS.ApplePayPaymentRequest | null = useMemo(() => {
    return {
      countryCode: shopId,
      currencyCode: currencyCode,
      supportedNetworks: ['amex', 'maestro', 'masterCard', 'visa', 'vPay'],
      merchantCapabilities: ['supports3DS'],
      shippingType: 'shipping',
      requiredBillingContactFields: ['postalAddress', 'name', 'phone', 'email'],
      requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
      total: {
        label: legalName,
        amount: currentTotalAmount.toFixed(2),
        type: 'final',
      },
      shippingMethods,
    };
  }, [currencyCode, currentTotalAmount, shippingMethods, shopId, legalName]);

  const generateApplePayErrors = useCallback(
    (errors: ApplePayError[]): ApplePayError[] => {
      const applePayErrors: ApplePayError[] = [];

      for (const error of errors) {
        const applePayError = new ApplePayError(
          error.code,
          error.contactField,
          formatMessage({
            id: `express-checkout:apple-pay:errors:${error.code}`,
          })
        );
        applePayErrors.push(applePayError);
      }

      return applePayErrors;
    },
    [formatMessage]
  );

  const generateApplePayPaymentData = useCallback(
    ({ payment }: { payment: ApplePayJS.ApplePayPayment }): ApplePaymentPaymentRequest => {
      return {
        method: 'applepay',
        checkoutId: payload.current.checkoutId,
        listParams: {
          summaryUrl: `${location.protocol}//${location.host}/checkout/confirm`,
          cancelUrl: `${location.protocol}//${location.host}/cart`,
          returnUrl: `${location.protocol}//${location.host}/checkout/confirm`,
        },
        cart: {
          items: payload.current.items,
          discount: {
            code: payload.current.discount?.code,
            type: payload.current.discount?.type,
          },
        },
        payment: {
          billingContact: payment.billingContact,
          shippingContact: payment.shippingContact,
          token: payment.token,
          shippingMethod: payload.current.shippingMethod,
        },
      };
    },
    [payload]
  );

  const handleValidateMerchant = useCallback(
    async (event: ApplePayJS.ApplePayValidateMerchantEvent): Promise<void> => {
      try {
        const merchantSession = await createApplePayMerchatSession(
          event.validationURL,
          window.location.hostname
        );
        if (applePaySession.current) {
          applePaySession.current.completeMerchantValidation(merchantSession);
        }
      } catch (error) {
        console.error(error);
        showErrorToast({
          error: 'error:unknown',
          caller: 'ApplePayButton - State',
        });
      }
    },
    []
  );

  const handleShippingContactSelected = useCallback(
    (event: ApplePayJS.ApplePayShippingContactSelectedEvent): void => {
      const { shippingContact } = event;
      const update = {
        newTotal: {
          label: legalName,
          amount: currentTotalAmount.toFixed(2),
          type: 'final' as const,
        },
      };

      const isServiceableCountry = shippingCountries.some(
        (country) => country === shippingContact.countryCode
      );

      if (!isServiceableCountry) {
        const errors: ApplePayError[] = [
          {
            code: 'addressUnserviceable',
            contactField: 'countryCode',
            message: formatMessage({
              id: 'express-checkout:apple-pay:errors:addressUnserviceable',
            }),
          },
        ];
        update['errors'] = generateApplePayErrors(errors);
      }

      if (applePaySession.current) {
        applePaySession.current.completeShippingContactSelection(update);
      }
    },
    [currentTotalAmount, formatMessage, generateApplePayErrors, shippingCountries, legalName]
  );

  const handlePaymentAuthorized = useCallback(
    async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
      const { payment } = event;
      const paymentData = generateApplePayPaymentData({ payment });

      try {
        const { redirectUrl, errors } = await createApplePayPayment({
          payment: paymentData,
        });
        if (errors) {
          if (applePaySession.current) {
            applePaySession.current.abort();
            applePaySession.current = null;

            // Get the customer session to retrieve the customer ID
            const sessionData = await getCheckoutSession();
            const customerId = isIdentifiedCustomer(sessionData) ? sessionData.customerId : null;

            if (customerId) {
              // Set the express checkout customer ID to include it in the effective cart payload
              setExpressCheckoutCustomerId(customerId);
            }

            await reFetchEffectiveCart();
            setIsApplePayModalOpen(true);
          }
        }

        const result = {
          status: ApplePaySession.STATUS_SUCCESS,
        };

        if (applePaySession.current) {
          applePaySession.current.completePayment(result);
        }

        if (result.status === ApplePaySession.STATUS_SUCCESS && redirectUrl) {
          push(redirectUrl);
        }
      } catch (error) {
        console.error(error);
        // If the request fails, return an error with a generic message.
        const result = {
          status: ApplePaySession.STATUS_FAILURE,
          errors: generateApplePayErrors([
            {
              code: 'unknown',
              contactField: undefined,
              message: formatMessage({
                id: 'express-checkout:apple-pay:errors:unknown',
              }),
            },
          ]),
        };

        if (applePaySession.current) {
          applePaySession.current.completePayment(result);
        }
      }
    },
    [
      push,
      formatMessage,
      generateApplePayErrors,
      generateApplePayPaymentData,
      reFetchEffectiveCart,
      setExpressCheckoutCustomerId,
      setIsApplePayModalOpen,
    ]
  );

  const handleShippingMethodSelected = useCallback(
    (event: ApplePayJS.ApplePayShippingMethodSelectedEvent): void => {
      const { amount, label } = event.shippingMethod;
      const totalAmount = itemsAmount + Number(amount);
      const update = {
        newTotal: {
          label: legalName,
          amount: totalAmount.toFixed(2),
          type: 'final' as const,
        },
        newLineItems: [
          {
            label: 'Subtotal',
            amount: itemsAmount.toFixed(2),
          },
          {
            label,
            amount: amount,
          },
        ],
      };
      if (request) {
        const selectedShippingMethod = request.shippingMethods?.find(
          (method) => method.label === label
        );

        if (selectedShippingMethod && applePaySession.current) {
          payload.current.shippingMethod = selectedShippingMethod.label;
          setCurrentTotalAmount(totalAmount);
          applePaySession.current.completeShippingMethodSelection(update);
        }
      }
    },
    [applePaySession, itemsAmount, request, legalName]
  );

  const handleApplePayEvents = useCallback(
    (applePaySession: ApplePaySession) => {
      applePaySession.onvalidatemerchant = handleValidateMerchant;
      applePaySession.onshippingcontactselected = handleShippingContactSelected;
      applePaySession.onshippingmethodselected = handleShippingMethodSelected;
      applePaySession.onpaymentauthorized = handlePaymentAuthorized;
    },
    [
      handlePaymentAuthorized,
      handleShippingContactSelected,
      handleShippingMethodSelected,
      handleValidateMerchant,
    ]
  );

  return {
    applePaySession,
    request,
    handleApplePayEvents,
  };
};

export default useApplePayButton;
